import * as Service from 'types/service';
import { ChronoUnit, Duration, Instant } from 'js-joda';
import { Theme, css } from '@emotion/react';
import { formatDuration } from 'utils/timeUtils';

import { IconTimer } from '@tocktix/pantry';
import { Typography } from 'bento';
import { useEffect, useState } from 'react';

const cssDefs = {
  container: css`
    display: flex;
  `,
  waitTimeContainer:
    ({ isLate }: { isLate: boolean }) =>
    ({ colors, spacing, borderRadius }: Theme) => css`
      align-items: center;
      background-color: ${isLate ? colors.bgDangerBase : colors.bgLight};
      border-radius: ${borderRadius[100]};
      display: flex;
      gap: ${spacing(0.5)}px;
      padding: 0 ${spacing(0.5)}px 0 ${spacing(0.5)}px;
    `,
  icon: css`
    height: 16px;
    width: 16px;
    vertical-align: middle;
  `,
};

interface Props {
  className?: string;
  party: Service.Party;
}

const getDurationInfo = (
  party: Service.Party
): {
  currentTimeWaited?: string;
  quotedTime?: string;
  isNowLate?: boolean;
} => {
  if (!party.arrivalTime) {
    return { currentTimeWaited: undefined, quotedTime: undefined, isNowLate: undefined };
  }

  const hasQuotedTimeMinutes = party.quotedTimeMinutes !== undefined;

  const arrivalInstant = Instant.ofEpochSecond(party.arrivalTime, 0);
  const secondsWaited = Duration.ofSeconds(arrivalInstant.until(Instant.now(), ChronoUnit.SECONDS));
  const isNowLate =
    hasQuotedTimeMinutes && party.quotedTimeMinutes
      ? secondsWaited.toMinutes() > party.quotedTimeMinutes
      : false;
  const quotedTime =
    hasQuotedTimeMinutes && party.quotedTimeMinutes
      ? formatDuration(Duration.ofMinutes(party.quotedTimeMinutes))
      : undefined;

  return {
    currentTimeWaited: formatDuration(secondsWaited),
    quotedTime: quotedTime,
    isNowLate: isNowLate,
  };
};

const PartyRedesignDuration: React.FC<Props> = ({ party }) => {
  const { currentTimeWaited, quotedTime, isNowLate } = getDurationInfo(party);
  const [timeString, setTimeString] = useState<string | undefined>(currentTimeWaited);
  const [isLate, setIsLate] = useState<boolean>(isNowLate ?? false);

  useEffect(() => {
    const updateTime = () => {
      const { currentTimeWaited, isNowLate } = getDurationInfo(party);
      setTimeString(currentTimeWaited);
      setIsLate(isNowLate ?? false);
    };

    updateTime();
    const interval = setInterval(updateTime, 15000);

    return () => {
      clearInterval(interval);
    };
  }, [party]);

  if (!timeString) {
    return null;
  }

  return (
    <div aria-label="Time since arrived" css={cssDefs.waitTimeContainer({ isLate })}>
      <IconTimer css={cssDefs.icon} />
      <Typography variant="body" color="default">
        {timeString}
        {quotedTime && `/${quotedTime}`}
      </Typography>
    </div>
  );
};

export default PartyRedesignDuration;
